import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faArrowUp, faArrowDown, faChevronDown } from '@fortawesome/free-solid-svg-icons';

const TRANSFORM_TYPES = [
  { value: 'url_encode', label: 'URL encode', category: 'Encoding', config: [] },
  { value: 'base64_encode', label: 'Base64 encode', category: 'Encoding', config: [] },
  { value: 'base64_decode', label: 'Base64 decode', category: 'Encoding', config: [] },
  { value: 'html_encode', label: 'HTML encode', category: 'Encoding', config: [] },
  { value: 'hex_encode', label: 'Hex encode', category: 'Encoding', config: [] },
  { value: 'md5', label: 'MD5 hash', category: 'Hash', config: [] },
  { value: 'sha1', label: 'SHA-1 hash', category: 'Hash', config: [] },
  { value: 'sha256', label: 'SHA-256 hash', category: 'Hash', config: [] },
  { value: 'prefix', label: 'Prefix', category: 'Modify', config: [{ key: 'value', label: 'Prefix text', type: 'text' }] },
  { value: 'suffix', label: 'Suffix', category: 'Modify', config: [{ key: 'value', label: 'Suffix text', type: 'text' }] },
  { value: 'match_replace', label: 'Match/Replace', category: 'Modify', config: [
    { key: 'match', label: 'Match', type: 'text' },
    { key: 'replace', label: 'Replace', type: 'text' },
  ]},
  { value: 'reverse', label: 'Reverse', category: 'Modify', config: [] },
  { value: 'case_modification', label: 'Case modify', category: 'Modify', config: [
    { key: 'mode', label: 'Mode', type: 'select', options: [
      { value: 'upper', label: 'UPPERCASE' },
      { value: 'lower', label: 'lowercase' },
      { value: 'capitalize', label: 'Capitalize' },
    ]}
  ]},
];

const getCategoryColor = (category) => {
  switch (category) {
    case 'Encoding': return { bg: 'bg-blue-500/20', text: 'text-blue-400' };
    case 'Hash': return { bg: 'bg-purple-500/20', text: 'text-purple-400' };
    case 'Modify': return { bg: 'bg-green-500/20', text: 'text-green-400' };
    default: return { bg: 'bg-gray-500/20', text: 'text-gray-400' };
  }
};

const IntruderProcessing = ({ processing, onProcessingChange, isDarkMode }) => {
  const [showAddMenu, setShowAddMenu] = useState(false);

  const addRule = (transformType) => {
    const typeDef = TRANSFORM_TYPES.find(t => t.value === transformType);
    const config = {};
    (typeDef?.config || []).forEach(c => {
      config[c.key] = c.type === 'select' ? c.options[0].value : '';
    });

    const newRule = {
      type: transformType,
      enabled: true,
      config,
    };

    onProcessingChange([...processing, newRule]);
    setShowAddMenu(false);
  };

  const removeRule = (index) => {
    const next = processing.filter((_, i) => i !== index);
    onProcessingChange(next);
  };

  const moveRule = (index, direction) => {
    const newIdx = index + direction;
    if (newIdx < 0 || newIdx >= processing.length) return;
    const next = [...processing];
    [next[index], next[newIdx]] = [next[newIdx], next[index]];
    onProcessingChange(next);
  };

  const updateRuleConfig = (index, key, value) => {
    const next = [...processing];
    next[index] = { ...next[index], config: { ...next[index].config, [key]: value } };
    onProcessingChange(next);
  };

  const toggleRule = (index) => {
    const next = [...processing];
    next[index] = { ...next[index], enabled: !next[index].enabled };
    onProcessingChange(next);
  };

  const getTypeDef = (type) => TRANSFORM_TYPES.find(t => t.value === type);

  // Group transforms by category for the add menu
  const grouped = TRANSFORM_TYPES.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className={`text-[10px] font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Payload Processing ({processing.length} rules)
        </label>
        <div className="relative">
          <button
            onClick={() => setShowAddMenu(!showAddMenu)}
            className="px-2.5 py-1 rounded text-[10px] font-medium bg-orange-600 text-white hover:bg-orange-700"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />Add rule
            <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-[8px]" />
          </button>

          {showAddMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowAddMenu(false)} />
              <div className={`absolute right-0 top-full mt-1 z-50 rounded-lg border shadow-lg w-56 py-1 ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                {Object.entries(grouped).map(([category, transforms]) => (
                  <div key={category}>
                    <div className={`px-3 py-1 text-[10px] font-semibold ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {category}
                    </div>
                    {transforms.map(t => (
                      <button
                        key={t.value}
                        onClick={() => addRule(t.value)}
                        className={`w-full text-left px-3 py-1 text-xs ${
                          isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Rules list */}
      {processing.length === 0 ? (
        <div className={`text-center py-6 text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
          No processing rules. Payloads will be used as-is.
        </div>
      ) : (
        <div className="space-y-1.5">
          {processing.map((rule, index) => {
            const typeDef = getTypeDef(rule.type);
            const catColor = getCategoryColor(typeDef?.category);
            return (
              <div
                key={index}
                className={`flex items-center gap-2 p-2 rounded border ${
                  rule.enabled
                    ? (isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200')
                    : (isDarkMode ? 'bg-gray-800/50 border-gray-700/50 opacity-50' : 'bg-gray-50 border-gray-200 opacity-50')
                }`}
              >
                <input
                  type="checkbox"
                  checked={rule.enabled}
                  onChange={() => toggleRule(index)}
                  className={`rounded ${isDarkMode ? 'accent-orange-500' : ''}`}
                />

                {/* Order controls */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveRule(index, -1)}
                    disabled={index === 0}
                    className={`text-[8px] px-1 ${isDarkMode ? 'text-gray-500 hover:text-gray-300 disabled:text-gray-700' : 'text-gray-400 hover:text-gray-600 disabled:text-gray-200'}`}
                  >
                    <FontAwesomeIcon icon={faArrowUp} />
                  </button>
                  <button
                    onClick={() => moveRule(index, 1)}
                    disabled={index === processing.length - 1}
                    className={`text-[8px] px-1 ${isDarkMode ? 'text-gray-500 hover:text-gray-300 disabled:text-gray-700' : 'text-gray-400 hover:text-gray-600 disabled:text-gray-200'}`}
                  >
                    <FontAwesomeIcon icon={faArrowDown} />
                  </button>
                </div>

                {/* Type badge */}
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${catColor.bg} ${catColor.text}`}>
                  {typeDef?.label || rule.type}
                </span>

                {/* Config inputs */}
                <div className="flex-1 flex items-center gap-2">
                  {(typeDef?.config || []).map(cfg => (
                    <div key={cfg.key} className="flex items-center gap-1">
                      <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{cfg.label}:</span>
                      {cfg.type === 'select' ? (
                        <select
                          value={rule.config?.[cfg.key] || ''}
                          onChange={e => updateRuleConfig(index, cfg.key, e.target.value)}
                          className={`text-[10px] rounded border px-1.5 py-0.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                        >
                          {cfg.options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={rule.config?.[cfg.key] || ''}
                          onChange={e => updateRuleConfig(index, cfg.key, e.target.value)}
                          className={`text-[10px] rounded border px-1.5 py-0.5 w-24 font-mono ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                          placeholder={cfg.label}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Delete */}
                <button
                  onClick={() => removeRule(index)}
                  className={`p-1 rounded text-[10px] ${isDarkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-500 hover:bg-red-50'}`}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Processing chain summary */}
      {processing.length > 0 && (
        <div className={`text-[10px] p-2 rounded ${isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-50 text-gray-400'}`}>
          Chain: payload → {processing.filter(r => r.enabled).map(r => getTypeDef(r.type)?.label || r.type).join(' → ')} → insert
        </div>
      )}
    </div>
  );
};

export default IntruderProcessing;
